/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable} from '@angular/core';
import {map, Observable} from 'rxjs';

import {transformJsonObjectProperty} from '../../components/util/adn-http-resource-transforms';
import {AdnApiClientService} from '../common/controller/adn-api-client.service';
import {
  SignalAdminResourceApiBase,
  SignalAdminResourceListParams,
  SignalAdminResourceModel,
  SignalAdminResourcePage
} from '../common/controller/signal-admin-resource-api';

export interface ReportTemplateModel extends SignalAdminResourceModel {
  [key: string]: any;
}

const transform = transformJsonObjectProperty('components.parameters');

@Injectable({providedIn: 'root'})
export class ReportTemplateApiService extends SignalAdminResourceApiBase<ReportTemplateModel> {
  constructor(client: AdnApiClientService) {
    super(client, 'reporttemplates');
  }

  override list(params: SignalAdminResourceListParams): Observable<SignalAdminResourcePage<ReportTemplateModel>> {
    return super.list(params).pipe(map((page) => ({
      ...page,
      results: (page.results || []).map((model) => transform.transformResponse(model))
    })));
  }

  override get(id: string): Observable<ReportTemplateModel> {
    return super.get(id).pipe(map((model) => transform.transformResponse(model)));
  }

  override save(model: ReportTemplateModel): Observable<ReportTemplateModel> {
    return super.save(transform.transformRequest(model)).pipe(
      map((saved) => transform.transformResponse(saved))
    );
  }

  listOptions(params: Record<string, any> = {}): Observable<SignalAdminResourcePage<ReportTemplateModel>> {
    return this.client.request<SignalAdminResourcePage<ReportTemplateModel>>({
      method: 'GET',
      url: this.resourceUri('reporttemplates'),
      params
    }).pipe(map((page) => ({
      ...page,
      results: (page.results || []).map((model) => transform.transformResponse(model))
    })));
  }

  listAll(): Observable<SignalAdminResourcePage<ReportTemplateModel>> {
    return this.listOptions({
      includeHidden: false,
      includeActive: true,
      includeInactive: false,
      orderBy: 'name',
      pageSize: 1001
    });
  }
}
